import Button from '@app/components/Button';
import Card from '@app/components/card/Card';
import CheckboxCardsList from '@app/components/CheckboxCardsList';
import InputBox from '@app/components/inputs/inputBox/InputBox';
import NakedFormInput from '@app/components/inputs/NakedFormInput';
import PermissionListItem from '@app/components/PermissionListItem';
import SaveCancelActions from '@app/components/SaveCancelActions';
import SVG from '@app/components/SVG';
import { H5StartTransparentBlack900 } from '@app/components/Text';
import { UserRolesType } from '@app/domain/user';
import { UserRoleType } from '@app/domain/userManagement/roleTypes';
import { UpdateWebUserRolesRequest } from '@app/domain/userManagement/users';
import { useLoadableSearchFilter } from '@app/hooks/useSearchFilter';
import useStagedMixedIds from '@app/hooks/useStagedMixedIds';
import ClearIcon from '@app/images/ic_close.svg';
import AppThemeProvider from '@app/routes/app/ThemeProvider';
import { mergeArrays } from '@app/utils/arrayUtils';
import { compare } from '@app/utils/comparatorUtils';
import Loadable from '@app/utils/Loadable';
import { isTruthy } from '@app/utils/utils';
import styled from '@emotion/styled';
import React, { FC, ReactNode, useState } from 'react';

export interface RoleState {
  isDisabled: boolean;
  isRemovalOnly?: boolean;
  tooltip: string | null;
}

export interface RolesModalElementIds {
  card: string;
  search: string;
  table: string;
  close: string;
  cancel: string;
  save: string;
}

interface Props {
  title: ReactNode;
  elementIds: RolesModalElementIds;
  contentColumnTitle: string;
  banner?: ReactNode;
  roleTypes: Loadable<UserRoleType[]>;
  initialActiveRoleIdsPerUser: string[][];
  canEdit: boolean;
  getRoleState?(roleId: string, roleType: UserRoleType | undefined): RoleState;
  confirmChange?(change: UpdateWebUserRolesRequest): Promise<boolean>;
  saveChange(change: UpdateWebUserRolesRequest): Promise<void>;
  onDone(): void;
}

// ROLE_KNOX_ADMIN is granted and revoked outside this UI only
const ROLE_KNOX_ADMIN_STATE: RoleState = { isDisabled: true, tooltip: 'This role cannot be changed here' };

interface RoleItem extends RoleState {
  id: string;
  displayName: string | null;
}

const RolesModal: FC<Props> = ({
  title,
  elementIds,
  contentColumnTitle,
  banner,
  roleTypes,
  initialActiveRoleIdsPerUser,
  canEdit,
  getRoleState,
  confirmChange,
  saveChange,
  onDone,
}) => {
  const { activeIds, indeterminateIds, addedIds, removedIds, changedIds, hasChanges, maxChanges, exceedsMaxChanges, cycle } =
    useStagedMixedIds(initialActiveRoleIdsPerUser);
  const [isSaving, setIsSaving] = useState<boolean>(false);

  const roles: Loadable<RoleItem[]> = roleTypes.map((loadedRoleTypes): RoleItem[] => {
    const roleTypesById = Object.fromEntries(loadedRoleTypes.map((roleType) => [roleType.id, roleType]));

    return mergeArrays([Object.keys(roleTypesById), ...initialActiveRoleIdsPerUser], true)
      .map((roleId): RoleItem => {
        const roleType: UserRoleType | undefined = roleTypesById[roleId];
        return {
          id: roleId,
          displayName: roleType?.displayName ?? null,
          ...(roleId === UserRolesType.adminRole
            ? ROLE_KNOX_ADMIN_STATE
            : (getRoleState?.(roleId, roleType) ?? { isDisabled: false, tooltip: null })),
        };
      })
      .sort(compare.byStringField((role) => role.id));
  });

  const disabledRoleIds = roles.result?.filter((role) => role.isDisabled).map((role) => role.id) ?? [];
  const removalOnlyRoleIds = roles.result?.filter((role) => role.isRemovalOnly).map((role) => role.id) ?? [];

  const [filteredData, searchTerm, setSearchTerm] = useLoadableSearchFilter(roles, {
    partialMatches(role) {
      return [role.id, role.displayName];
    },
  });

  async function onSave(): Promise<void> {
    const change: UpdateWebUserRolesRequest = { addedWebAppRoleIds: addedIds, removedWebAppRoleIds: removedIds };
    if (confirmChange && !(await confirmChange(change))) {
      return;
    }

    setIsSaving(true);

    try {
      await saveChange(change);
      onDone();
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <AppThemeProvider>
      <MainCard id={elementIds.card}>
        <TitleLine>
          <Title>
            <H5StartTransparentBlack900.div>{title}</H5StartTransparentBlack900.div>
          </Title>
          <CloseButton id={elementIds.close} appearance='text' colorScheme='primary' onClick={onDone}>
            <SVG accessibilityLabel='close' image={ClearIcon} height={30} />
          </CloseButton>
        </TitleLine>
        {banner}
        <InputBox appearance='line' colorScheme='secondary'>
          <NakedFormInput
            name={elementIds.search}
            dataTestId={elementIds.search}
            type='text'
            colorScheme='primary'
            placeholderStyle='onlyWhenEmpty'
            heightType='thick'
            clearable
            placeholder='🔍 Search for Id / Name'
            value={searchTerm}
            onChange={(newValue): void => setSearchTerm(newValue ?? '')}
            autoFocus
          />
        </InputBox>
        <TableDiv>
          <CheckboxCardsList
            id={elementIds.table}
            contentColumnTitle={contentColumnTitle}
            readonly={!canEdit || isSaving}
            activeIds={activeIds}
            indeterminateIds={indeterminateIds}
            changedIds={changedIds}
            changingIds={[]}
            allItems={filteredData}
            disableItem={(roleId: string): boolean => disabledRoleIds.includes(roleId)}
            itemRenderer={(role: RoleItem): ReactNode => (
              <PermissionListItem permissionsId={role.id} displayName={role.displayName} tooltip={role.tooltip} />
            )}
            itemKeywordsGetter={(role: RoleItem): string[] => [role.id, role.displayName].filter(isTruthy)}
            // The next state comes from the cycle, not from which way the checkbox flipped
            onAdd={async (roleId): Promise<void> => cycle(roleId, { removalOnly: removalOnlyRoleIds.includes(roleId) })}
            onRemove={async (roleId): Promise<void> => cycle(roleId, { removalOnly: removalOnlyRoleIds.includes(roleId) })}
          />
        </TableDiv>
        {canEdit && (
          <SaveCancelActions
            cancelId={elementIds.cancel}
            saveId={elementIds.save}
            hasChanges={hasChanges}
            maxChanges={maxChanges}
            exceedsMaxChanges={exceedsMaxChanges}
            isSaving={isSaving}
            onCancel={onDone}
            onSave={onSave}
          />
        )}
      </MainCard>
    </AppThemeProvider>
  );
};

export default RolesModal;

const MainCard = styled(Card)`
  width: min(900px, 90vw);
  min-height: 600px;
  max-height: 70vh;

  padding: 26px 32px;
  position: relative;

  display: flex;
  flex-direction: column;
  justify-content: flex-start;
`;

const TitleLine = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  flex: 0 0 auto;

  margin-bottom: 4px;
`;

const Title = styled.div`
  & > *:not(:first-child) {
    margin-top: 8px;
  }
`;

const CloseButton = styled(Button)`
  font-size: 16px;
  font-weight: 400;
  line-height: normal;
  transition: 0.1s all ease-in-out;
  padding: 4px 6px 5px;
  margin-bottom: 3px;
`;

const TableDiv = styled.div`
  overflow-y: scroll !important;
`;
